import { GlobeAltIcon } from '@heroicons/react/outline';
import { Modal } from 'antd';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import ButtonDefault, {
  ButtonVariants,
} from '@/components/shared/basic/button';
import { ShouldRender } from '@/components/shared/basic/ShouldRender';
import { formateErrorObject } from '@/components/shared/formateErrorObject';
import { notify } from '@/components/shared/notify';

import { cartAtom } from '@/atoms/Cart';
import { countryAtom } from '@/atoms/countryState';
import { localUserID, user } from '@/atoms/user';
import { addToCart, getActiveCountries, getCartSummary } from '@/services/Cart';
import { globalCountryChange } from '@/utils/GlobalCountry';
import { CartItem } from '@/utils/interfaces/cart';
import { User } from '@/utils/interfaces/User';

export interface CountryData {
  code: string;
  currency: string;
  phoneCode: string;
  phonecode?: string;
  countryId: number;
  name: string;
  currency_symbol?: string;
  timezone?: string;
}
export interface StoredCountry {
  country: CountryData;
  source: 'manual' | 'auto';
}

const STORAGE_KEY = 'zebra_selected_country';

const CountrySelector: React.FC<{ loggedIn: boolean }> = ({ loggedIn }) => {
  const [countryState, setCountryState] = useRecoilState(countryAtom);
  const { isOpen, selectedCountry } = countryState;
  const [isBelowXL, setIsBelowXL] = useState(false);
  const [countryList, setCountryList] = useState<CountryData[]>([]);
  const [isHydrated, setIsHydrated] = useState(false);
  const cartData = useRecoilValue(cartAtom);
  const localUserId = useRecoilValue(localUserID);
  const userData: User | undefined = useRecoilValue(user);
  const router = useRouter();
  const getFlagUrl = useCallback(
    (code: string) => `https://flagcdn.com/w40/${code.toLowerCase()}.webp`,
    [],
  );

  const setLocalStorageWithExpiry = (
    key: string,
    value: any,
    ttlHours = 24,
  ) => {
    if (typeof localStorage === 'undefined') return;
    const now = new Date();
    const item = {
      value,
      expiry: now.getTime() + ttlHours * 60 * 60 * 1000,
    };
    localStorage.setItem(key, JSON.stringify(item));
  };

  const getLocalStorageWithExpiry = (key: string) => {
    if (typeof localStorage === 'undefined') return null;
    const itemStr = localStorage.getItem(key);
    if (!itemStr) return null;
    try {
      const item = JSON.parse(itemStr);
      if (new Date().getTime() > item.expiry) {
        localStorage.removeItem(key);
        return null;
      }
      return item.value;
    } catch {
      localStorage.removeItem(key);
      return null;
    }
  };

  const handleBulkAddToCart = async (cartData: CartItem[]) => {
    try {
      await getCartSummary();

      if (!cartData || !Array.isArray(cartData)) {
        return;
      }

      const addCartPromises = cartData.map(async (item) => {
        const payload = {
          id: item.product_id as number,
          temp_user_id: localUserId,
          quantity: item.quantity || 1,
          trackingData: {
            productData: {
              price: Number(item.price),
              id: Number(item.product_id),
              heading: item.name,
              quantity: item.quantity || 1,
              currency: item?.currency_value,
            },
            userData,
            pathname: router.pathname,
            productType: item.type === 'Book' ? 'book' : 'course',
            type:
              item.type === 'Bundle'
                ? 'bundles'
                : item.type === 'Book'
                  ? 'books'
                  : 'courses',
          },
        };

        const response = await addToCart(payload);

        return response?.data;
      });

      const responses = await Promise.all(addCartPromises);

      const lastCart = responses[responses.length - 1];
      const cartId = lastCart?.cart_id || lastCart?.data?.cart_id;

      if (router.asPath.includes('/cart/?cartStep=1')) {
        router.replace(`/cart/?cartStep=1&cartId=${cartId}`);
      } else if (router.asPath.includes('/checkout')) {
        router.replace(`/checkout/?cartId=${cartId}`);
      } else {
        router.replace(`/cart/?cartId=${cartId}`);
      }
    } catch (error: any) {
      const message = formateErrorObject(error);
      notify(message, 'error');
      router.replace('/books/shop/?price=paid');
    } finally {
      setCountryState((prev) => ({
        ...prev,
        globalCountryChange: false,
      }));
    }
  };

  const fallbackCountry = useMemo<CountryData | null>(() => {
    return countryList.find((c) => c.code === 'IN') || null;
  }, [countryList]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleResize = () => setIsBelowXL(window.innerWidth < 1386);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const setCountry = useCallback(
    async (country: CountryData, source: 'manual' | 'auto') => {
      setCountryState({
        isOpen: false,
        selectedCountry: {
          ...country,
          currency: country.currency || 'INR',
          currency_symbol: country.currency_symbol || '₹',
          timezone: country.timezone || 'Asia/Kolkata',
        },
        source,
      });

      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('/api/v4/') && key !== '/api/v4/active-countries') {
          localStorage.removeItem(key);
        }
      });

      if (source === 'manual') {
        const data: StoredCountry = { country, source };
        setLocalStorageWithExpiry(STORAGE_KEY, data);

        document.cookie = `country=${country.code}; path=/; max-age=${60 * 60 * 24}; SameSite=Lax`;
        if (
          router.asPath.includes('/cart/') ||
          router.asPath.includes('/checkout/')
        ) {
          try {
            await handleBulkAddToCart(cartData);
          } catch (err: any) {
            notify(formateErrorObject(err), 'error');
          }
        }
      } else {
        sessionStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ country, source }),
        );

        document.cookie = `country=${country.code}; path=/; max-age=${60 * 60 * 24
          }; SameSite=Lax`;
      }
    },
    [setCountryState, router.asPath, cartData],
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const parsed: StoredCountry | null = getLocalStorageWithExpiry(STORAGE_KEY);

    if (parsed) {
      if (parsed.source === 'manual') {
        setCountryState((prev) => ({
          ...prev,
          selectedCountry: {
            ...parsed.country,
            currency: parsed.country.currency || 'INR',
            currency_symbol: parsed.country.currency_symbol || '₹',
          },
          source: 'manual',
        }));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    }

    setIsHydrated(true);
  }, [setCountryState]);

  useEffect(() => {
    if (!isHydrated || selectedCountry) return;
    const controller = new AbortController();

    const detectCountry = async () => {
      const sources = [
        {
          url: 'https://ipwho.is/',
          extract: (data: any) => ({
            code: data.country_code,
            timezone: data.timezone?.id || null,
          }),
        },
        {
          url: 'https://ipinfo.io/json',
          extract: (data: any) => ({
            code: data.country,
            timezone: data.timezone || null,
          }),
        },
      ];

      for (const source of sources) {
        try {
          const res = await fetch(source.url, { signal: controller.signal });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();

          const { code, timezone } = source.extract(data);
          if (!code) throw new Error('Missing country code');

          const found = countryList.find((c) => c.code === code);
          const country = found || fallbackCountry;
          if (!country) continue;

          const countryWithTZ = {
            ...country,
            timezone: timezone || 'Asia/Kolkata',
          };
          setCountry(countryWithTZ, 'manual');
          return;
        } catch (err) {
          continue;
        }
      }

      if (fallbackCountry) {
        setCountry({ ...fallbackCountry, timezone: 'Asia/Kolkata' }, 'manual');
      }
    };

    detectCountry();
    return () => controller.abort();
  }, [isHydrated, selectedCountry, countryList, fallbackCountry, setCountry]);

  useEffect(() => {
    const syncCountry = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && e.newValue) {
        const parsed: StoredCountry = JSON.parse(e.newValue);
        if (parsed.source === 'manual') {
          setCountryState((prev) => ({
            ...prev,
            selectedCountry: {
              ...parsed.country,
              currency: parsed.country.currency || 'INR',
              currency_symbol: parsed.country.currency_symbol || '₹',
              timezone: parsed.country.timezone || 'Asia/Kolkata',
            },
            source: 'manual',
          }));
        }
      }
    };
    window.addEventListener('storage', syncCountry);
    return () => window.removeEventListener('storage', syncCountry);
  }, [setCountryState]);

  // const handleCountryChange = (code: string) => {
  //   if (typeof window === 'undefined') return;

  //   const country = countryList.find((c) => c.code === code) || fallbackCountry;
  //   if (!country) return;

  //   setCountry(country, 'manual');
  //   if (
  //     !router.asPath.includes('/cart/') &&
  //     !router.asPath.includes('/checkout/')
  //   ) {
  //     setTimeout(() => window.location.reload(), 300);
  //   }
  // };

  useEffect(() => {
    if (typeof window === 'undefined' || !isHydrated) return;

    const localData: StoredCountry | null =
      getLocalStorageWithExpiry(STORAGE_KEY);
    if (!localData || !localData.country) return;

    const recoilCode = selectedCountry?.code;
    const localCode = localData.country.code;

    if (localCode && recoilCode && localCode !== recoilCode) {
      setCountryState((prev) => ({
        ...prev,
        selectedCountry: {
          ...localData.country,
          currency: localData.country.currency || 'INR',
          currency_symbol: localData.country.currency_symbol || '₹',
          timezone: localData.country.timezone || 'Asia/Kolkata',
        },
        source: localData.source || 'manual',
      }));
    }
  }, [isHydrated, selectedCountry, setCountryState]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const data: CountryData[] = await getActiveCountries();
        setCountryList(
          data?.map((data) => ({ ...data, phoneCode: data.phonecode || '' })) ||
          [],
        );
      } catch (error) {
        console.error('Failed to load countries:', error);
      }
    };
    fetchCountries();
  }, []);

  const showSelectCountry = router.pathname === '/';

  if (!isHydrated) return null;

  return (
    <>
      <ShouldRender check={!loggedIn || !isBelowXL}>
        <AnimatePresence>
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className={showSelectCountry ? '' : 'hidden'}
          >
            <motion.div
              className='flex items-center'
              onClick={() => {
                let globalCountryChange = false;
                if (
                  router.asPath.includes('/cart/') ||
                  router.asPath.includes('/checkout/')
                ) {
                  globalCountryChange = true;
                }
                setCountryState((prev) => ({
                  ...prev,
                  isOpen: true,
                  globalCountryChange,
                }));
              }}
            >
              <ButtonDefault
                variant={ButtonVariants.UNSTYLED}
                size={0}
                className='xl:w-10 w-8 xl:h-10 h-[32px] rounded-xl border-[1.5px] border-grayZl-900 hover:bg-grayZl-850 transition-all sm:flex hidden items-center justify-center hover:scale-105 active:scale-95'
              >
                {selectedCountry ? (
                  <>
                    <div className='relative w-4 h-3  md:w-5 md:h-4'>
                      <Image
                        src={getFlagUrl(selectedCountry.code)}
                        alt={selectedCountry.name}
                        fill
                        sizes='20px'
                        className='object-cover rounded-sm'
                      />
                    </div>

                    {/* <span className='text-grayZl-800'>|</span> */}

                    {/* <p className='font-medium text-xs md:text-sm'>
                    {selectedCountry.currency}
                  </p> */}
                  </>
                ) : (
                  <>
                    <GlobeAltIcon className='w-5 h-5' />
                    <p className='ml-1'>Select</p>
                  </>
                )}
              </ButtonDefault>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </ShouldRender>

      <Modal
        title='Select your country'
        open={isOpen}
        onCancel={() => setCountryState((prev) => ({ ...prev, isOpen: false }))}
        footer={null}
        centered
        classNames={{ body: 'p-2 md:p-5' }}
        destroyOnHidden
      >
        <div className='grid grid-cols-2 sm:grid-cols-3 gap-4'>
          {countryList.map((country) => (
            <div
              key={country.code}
              onClick={() => {
                // handleCountryChange(country.code)
                globalCountryChange({
                  cartData,
                  code: country.code,
                  countryList,
                  fallbackCountry,
                  localUserId,
                  router,
                  setCountryState,
                  source: 'manual',
                  userData
                })
              }}
              className={clsx(

                'flex flex-col items-center justify-center gap-2 p-3 rounded-xl border border-transparent hover:border-grayZl-700 hover:bg-grayZl-750 cursor-pointer transition-all',
                selectedCountry?.code === country.code &&
                'border-grayZl-950 bg-grayZl-850',
              )}
            >
              <div className='relative w-10 h-8'>
                <Image
                  src={getFlagUrl(country.code)}
                  alt={country.name}
                  fill
                  sizes='40px'
                  className='object-cover rounded shadow-sm'
                />
              </div>
              <p className='text-sm font-medium text-white text-center'>
                {country.name}
              </p>
              <p className='text-xs text-white/60'>
                {country.currency} (+{country.phoneCode})
              </p>
            </div>
          ))}
        </div>
      </Modal>
    </>
  );
};

export default CountrySelector;
